import { useEffect, useState } from "react";

/**
 * How much of the layout viewport the visual viewport has to lose before it
 * counts as a keyboard. Browser chrome collapsing on scroll moves it by less
 * than this; the shortest iOS keyboard, with no suggestion bar, takes ~260px.
 */
export const KEYBOARD_MIN_PX = 150;

/** Pure half of the hook, so the threshold can be tested without a browser. */
export function keyboardOpen(layoutHeight: number, visualHeight: number): boolean {
  return layoutHeight - visualHeight > KEYBOARD_MIN_PX;
}

/**
 * Whether the on-screen keyboard is up — read off `visualViewport` shrinking
 * rather than off focus, because a focused field on a desktop has no keyboard
 * and hiding the tab bar there would be a bug.
 *
 * The floating primary action and the tab bar both hide on this: with the
 * keyboard up they would sit on top of it, over the field being typed in.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const measure = () => setOpen(keyboardOpen(window.innerHeight, viewport.height));
    measure();
    viewport.addEventListener("resize", measure);
    return () => viewport.removeEventListener("resize", measure);
  }, []);

  return open;
}
